import {useMutation} from '@apollo/client';
import {message} from 'antd';
import {useTranslation} from 'react-i18next';
import {getErrorKeys, showError} from '../../../../utils/exceptionUtils';
import {
    REVISE_POP_REVIEW_ACTION,
    RevisePopReviewActionResponse,
    SUBMIT_POP_REVIEW_ACTION,
    SubmitPopReviewActionResponse
} from '../graphql/mutations/popReviewMutations';
import {PopReviewAction} from '../graphql/types/PopReviewType';

/**
 * POP 审核提交（一次一单，批量由调用方循环调用）
 *
 * 审核员不由前端传，后端取当前登录账号以防伪造审核人（与 POD 传 reviewerAccountId 不同）。
 */
/** 后端审核单状态乐观锁的 messageKey（该单已被他人审完时提交/改判都会撞上） */
const REVIEW_TASK_STATE_INVALID_KEY = 'errors.business.reviewTask.stateInvalid';

export const usePopReviewActions = (
    /** 撞上状态乐观锁时回调，供调用方刷新详情展示他人的审核结论 */
    onStateConflict?: () => void
) => {
    const {t} = useTranslation();
    const [submitAction, {loading: submitLoading}] = useMutation<SubmitPopReviewActionResponse>(
        SUBMIT_POP_REVIEW_ACTION
    );
    const [reviseAction, {loading: reviseLoading}] = useMutation<RevisePopReviewActionResponse>(
        REVISE_POP_REVIEW_ACTION
    );
    const loading = submitLoading || reviseLoading;

    const handleReview = async (
        reviewTaskId: string,
        action: PopReviewAction,
        disqualifyReasonCodes?: string[],
        reviewRemark?: string,
        reviewRemarkLang?: string,
        /** true 为改判：对已审核的单重新提交结论（后端走改判状态校验） */
        revision?: boolean
    ): Promise<boolean> => {
        // 判定「不合格」必须给出原因，否则后端会拒绝；此处前置拦截以免无效请求
        if (action === PopReviewAction.REJECT && !disqualifyReasonCodes?.length) {
            message.error(t('popReview.messages.reasonRequired'));
            return false;
        }

        // 「其他不合规情况」必须补充备注说明
        if (disqualifyReasonCodes?.includes('OTHER_NONCOMPLIANCE')
                && !reviewRemark?.trim()) {
            message.error(t('popReview.messages.remarkRequiredForOther'));
            return false;
        }

        try {
            const variables = {
                reviewTaskId,
                action,
                disqualifyReasonCodes: disqualifyReasonCodes?.length ? disqualifyReasonCodes : null,
                reviewRemark: reviewRemark || null,
                // 无备注时不必传语种（后端仅在有备注时构造富化 JSON）
                reviewRemarkLang: reviewRemark ? reviewRemarkLang || null : null
            };
            const result = revision
                ? (await reviseAction({variables})).data?.revisePopReviewAction
                : (await submitAction({variables})).data?.submitPopReviewAction;

            if (result) {
                const actionText =
                    action === PopReviewAction.APPROVE
                        ? t('popReview.messages.approved')
                        : t('popReview.messages.rejected');
                message.success(t('popReview.messages.reviewSuccess', {action: actionText}));
                return true;
            }

            message.error(t('popReview.messages.reviewFailed'));
            return false;
        } catch (error) {
            if (getErrorKeys(error).messageKey === REVIEW_TASK_STATE_INVALID_KEY) {
                message.error(t('popReview.messages.reviewedByOther'));
                onStateConflict?.();
                return false;
            }
            showError(error);
            return false;
        }
    };

    return {
        handleReview,
        loading
    };
};
